<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Projet Personnel</div>
        <h1 class="hero-title">PikuTrade</h1>
        <p class="hero-subtitle">Bot de trading crypto intelligent avec IA</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-calendar"></i> Décembre 2024</span>
          <span class="meta-item"><i class="mdi mdi-account-group"></i> Équipe de 3</span>
          <span class="meta-item"><i class="mdi mdi-shield-lock"></i> Serveur SSH sécurisé</span>
        </div>
      </div>
      <div class="hero-image">
        <img :src="require('@/assets/image/pikuTradeLogo.png')" alt="Logo PikuTrade"/>
      </div>
    </header>

    <!-- Overview Card -->
    <section class="card overview-card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Vue d'ensemble</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          <strong>PikuTrade</strong> est une initiative personnelle portée par une équipe de trois passionnés,
          mêlant programmation et finance. Ce projet vise à développer un <strong>bot de trading crypto</strong>
          performant, intégrant une API robuste et une intelligence artificielle pour analyser les données en
          <strong>temps réel</strong>.
        </p>
        <p>
          Lancé en <strong>décembre 2024</strong>, le projet a été conçu avec un fort accent sur la sécurité,
          notamment grâce à un <strong>serveur dédié sécurisé via SSH</strong>. L'objectif principal est de
          créer un système capable de prendre des décisions de trading automatiques basées sur l'analyse de
          marché et des prédictions d'intelligence artificielle, tout en respectant les contraintes de l'API
          Bitget et en maintenant une architecture évolutive.
        </p>
        <div class="tech-stack">
          <span class="tech-badge">Java Spring Boot</span>
          <span class="tech-badge">DL4J</span>
          <span class="tech-badge">API Bitget</span>
          <span class="tech-badge">SSH</span>
          <span class="tech-badge">Cryptomonnaies</span>
          <span class="tech-badge">Machine Learning</span>
        </div>
      </div>
    </section>

    <!-- API Section -->
    <section class="card api-card">
      <div class="card-header">
        <i class="mdi mdi-api"></i>
        <h2>Architecture API</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Développement avec Spring Boot</h3>
        <p>
          Développée avec <strong>Java Spring Boot</strong>, l'API est le moteur principal de PikuTrade,
          gérant les interactions avec l'API Bitget et le traitement des données. Elle constitue le cœur
          du système et permet une communication fluide entre tous les composants.
        </p>
        <p>
          L'architecture a été pensée pour répondre à plusieurs défis techniques majeurs, notamment la
          gestion des limitations de l'API Bitget (rate limiting), l'optimisation des appels réseau, et
          la nécessité de prendre des décisions rapides basées sur des données en constante évolution.
        </p>

        <div class="feature-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-flash"></i>
            </div>
            <h4>Batcher Asynchrone</h4>
            <p>Mise en place d'un système de batcher asynchrone permettant de gérer les limitations
            imposées par l'API Bitget (rate limiting) et d'optimiser les appels API. Ce système regroupe
            intelligemment les requêtes pour maximiser l'efficacité tout en respectant les contraintes.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-clock-outline"></i>
            </div>
            <h4>Scheduler Intelligent</h4>
            <p>Conception d'un scheduler intelligent pour calculer les meilleurs prix d'entrée et de
            sortie en fonction des fluctuations du marché. Il analyse en continu les données et ajuste
            les stratégies de trading en temps réel.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-brain"></i>
            </div>
            <h4>Intégration IA</h4>
            <p>Intégration avec l'intelligence artificielle pour permettre des analyses prédictives
            des données de marché. Le système communique avec le modèle DL4J pour obtenir des prédictions
            et ajuster automatiquement les stratégies.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-cube-outline"></i>
            </div>
            <h4>Architecture SOLID</h4>
            <p>Application stricte des principes SOLID, assurant une architecture flexible et évolutive,
            adaptée aux exigences du projet. Le code est modulaire, testable et facile à maintenir,
            permettant l'ajout de nouvelles fonctionnalités sans refonte majeure.</p>
          </div>
        </div>

        <div class="image-showcase">
          <img :src="require('@/assets/image/api-endpoints.png')" alt="API Endpoints"/>
          <span class="image-caption">Exemples de requêtes API</span>
        </div>
      </div>
    </section>

    <!-- AI Section -->
    <section class="card ai-card">
      <div class="card-header">
        <i class="mdi mdi-head-lightbulb"></i>
        <h2>Intelligence Artificielle</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Deep Learning avec DL4J</h3>
        <p>
          L'intelligence artificielle est un pilier clé de PikuTrade, développée avec la bibliothèque
          <strong>DL4J (Deeplearning4j)</strong>. Cette composante permet d'analyser les tendances du
          marché et d'ajuster automatiquement les stratégies de trading en fonction des données
          historiques et en temps réel.
        </p>
        <p>
          Le modèle a été entraîné sur des données historiques provenant de l'API Bitget, permettant
          d'identifier des patterns complexes et de prédire les mouvements de prix avec une précision
          croissante. L'utilisation de DL4J a permis de tirer parti de ses capacités de parallélisme
          et de traitement des données, garantissant des prédictions rapides et précises même dans un
          environnement de trading haute fréquence.
        </p>

        <div class="capability-list">
          <div class="capability-item">
            <i class="mdi mdi-chart-line"></i>
            <span>Prédiction des tendances basée sur l'historique de marché</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-sync"></i>
            <span>Analyse temps réel pour ajustements automatiques</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-database"></i>
            <span>Entraînement continu sur données Bitget</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-speedometer"></i>
            <span>Prédictions rapides grâce au parallélisme DL4J</span>
          </div>
        </div>

        <div class="image-showcase">
          <img :src="require('@/assets/image/api-endpoints.png')" alt="Prédictions IA"/>
          <span class="image-caption">Visualisation des prédictions du modèle</span>
        </div>
      </div>
    </section>

    <!-- IUT Competencies Validation -->
    <section class="card validation-card">
      <div class="card-header">
        <i class="mdi mdi-certificate-outline"></i>
        <h2>Validation des Compétences IUT</h2>
      </div>
      <div class="card-content">
        <div class="validation-grid">
          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">UE1.1</span>
              <h4>Réaliser : Implémentation</h4>
            </div>
            <p><strong>Pourquoi :</strong> Créer une solution robuste capable de traiter des flux de données financiers massifs en temps réel.</p>
            <p><strong>Comment :</strong> Développement d'une architecture backend performante avec <strong>Spring Boot</strong> et intégration de l'API Bitget via des WebSockets.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">UE3.2</span>
              <h4>Optimiser : Algorithmique Avancée</h4>
            </div>
            <p><strong>Pourquoi :</strong> Maximiser les gains et minimiser les risques grâce à des décisions basées sur des probabilités précises.</p>
            <p><strong>Comment :</strong> Développement et entraînement d'un modèle de Deep Learning avec <strong>DL4J</strong> pour prédire les tendances de marché.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">CE1.04</span>
              <h4>Réaliser : Qualité & Documentation</h4>
            </div>
            <p><strong>Pourquoi :</strong> Faciliter la maintenance et l'évolution du bot par une équipe pluridisciplinaire.</p>
            <p><strong>Comment :</strong> Application stricte des principes <strong>SOLID</strong>, Clean Code et documentation complète des endpoints API.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Outcomes -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-lightbulb-on"></i>
        <h2>Compétences Développées</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Développement Backend</h4>
            <ul>
              <li>Architecture d'API REST avec Spring Boot</li>
              <li>Gestion asynchrone haute fréquence</li>
              <li>Optimisation des performances</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Intelligence Artificielle</h4>
            <ul>
              <li>Implémentation de modèles DL4J</li>
              <li>Analyse prédictive financière</li>
              <li>Deep learning pour trading</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Sécurité & DevOps</h4>
            <ul>
              <li>Configuration serveur SSH</li>
              <li>Gestion données sensibles</li>
              <li>Déploiement sécurisé</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Gestion de Projet</h4>
            <ul>
              <li>Travail en équipe de 3</li>
              <li>Planification et deadlines</li>
              <li>Collaboration technique</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class PikuTradeComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

/* Hero Section */
.hero-section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(19, 124, 139, 0.2);
  color: var(--primary-color);
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
}

.hero-title {
  font-size: 3.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.4em;
  color: var(--sub-color);
  margin-bottom: 24px;
  font-weight: 400;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.95em;
}

.meta-item i {
  color: var(--primary-color);
}

.hero-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(19, 124, 139, 0.3);
}

/* Card Styles */
.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.2);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  font-weight: 700;
  color: var(--h2-color);
  margin: 0;
}

.card-content {
  color: var(--text-color);
}

.lead-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 24px;
  color: var(--text-color);
}

.section-subtitle {
  font-size: 1.4em;
  color: var(--h2-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

/* Tech Stack */
.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
  transition: all 0.3s ease;
}

.tech-badge:hover {
  background: rgba(19, 124, 139, 0.25);
  transform: translateY(-2px);
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin: 32px 0;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.feature-icon {
  width: 50px;
  height: 50px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.feature-icon i {
  font-size: 24px;
  color: var(--primary-color);
}

.feature-item h4 {
  font-size: 1.1em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.feature-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
  line-height: 1.6;
}

/* Capability List */
.capability-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0;
}

.capability-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
  transition: all 0.3s ease;
}

.capability-item:hover {
  background: var(--dark-bg-card);
  transform: translateX(8px);
}

.capability-item i {
  font-size: 24px;
  color: var(--primary-color);
}

.capability-item span {
  color: var(--text-color);
  font-size: 1em;
}

/* Validation Card Styles */
.validation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.validation-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  border-top: 4px solid var(--primary-color);
}

.v-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.v-code {
  padding: 4px 10px;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.9em;
  white-space: nowrap;
}

.v-header h4 {
  margin: 0;
  color: var(--h2-color);
  font-size: 1.1em;
}

.validation-item p {
  font-size: 0.95em;
  margin: 8px 0;
  line-height: 1.6;
}

.validation-item strong {
  color: var(--primary-color);
}

/* Learning Grid */
.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.learning-category {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.learning-category h4 {
  font-size: 1.2em;
  color: var(--primary-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.learning-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-category li {
  padding: 8px 0 8px 24px;
  color: var(--text-color);
  position: relative;
  font-size: 0.95em;
}

.learning-category li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

/* Responsive */
@media (max-width: 768px) {
  .project-container {
    padding-left: 20px;
  }

  .hero-section {
    grid-template-columns: 1fr;
  }

  .hero-title {
    font-size: 2.5em;
  }

  .card {
    padding: 24px;
  }
}
</style>